'use strict';

/**
 * Plansheet brand chrome.
 *
 * One stylesheet, registered over the Pict CSS cascade at a high priority, that
 * gives the whole app the drafting-sheet feel the marketing site has: Gorton
 * Perfected (the proportional revival of engraving instrument lettering) on the
 * "drafting layer" only -- headings, navigation, buttons, badges, table heads,
 * tabs, the top bar, the sign-in wordmark -- set uppercase and letterspaced the
 * way drawing lettering is. Body copy, form values, and long prose stay on the
 * readable system sans the themes already define.
 *
 * It rides on top of every palette (Plan Sheet, Blueprint, Drafting Paper, and
 * the kit themes), so it only ever references theme color tokens, never a fixed
 * color. The exceptions are the corners and borders: sheets have crisp corners
 * and visible linework, so the brand layer squares the rounded pills the stock
 * chrome ships with and firms up a few borders. Amber stays measurement ink and
 * is left to the themes.
 *
 * Registered from the Application:
 *   this.pict.CSSMap.addCSS('Plansheet-Brand-Chrome', libBrandChrome, 1200);
 * Priority 1200 sits above the pict-view default CSS priority (1000), so
 * equal-specificity chrome rules win on the cascade -- including the body
 * content font, which the Shell view also declares.
 */

module.exports = /*css*/`
/* ---- The drafting face: Gorton Perfected (commercial, self-hosted) ------ */
/* The proportional revival of the engraving lettering -- five weights so the
   drafting layer can range from hairline display to bold labels. */

@font-face
{
	font-family: 'Gorton Perfected';
	src: url('/fonts/GortonPerfected-Light.woff2') format('woff2');
	font-weight: 300;
	font-display: swap;
}
@font-face
{
	font-family: 'Gorton Perfected';
	src: url('/fonts/GortonPerfected-Regular.woff2') format('woff2');
	font-weight: 400;
	font-display: swap;
}
@font-face
{
	font-family: 'Gorton Perfected';
	src: url('/fonts/GortonPerfected-Medium.woff2') format('woff2');
	font-weight: 500;
	font-display: swap;
}
@font-face
{
	font-family: 'Gorton Perfected';
	src: url('/fonts/GortonPerfected-Semibold.woff2') format('woff2');
	font-weight: 600;
	font-display: swap;
}
@font-face
{
	font-family: 'Gorton Perfected';
	src: url('/fonts/GortonPerfected-Bold.woff2') format('woff2');
	font-weight: 700;
	font-display: swap;
}

/* ---- The content face: IBM Plex Sans (SIL OFL) ------------------------- */
/* The companion to Gorton: a technical text sans for everything you read --
   body copy, the navigation rail, table cells, typed values. Self-hosted. */

@font-face
{
	font-family: 'IBM Plex Sans';
	src: url('/fonts/IBMPlexSans-Regular.woff2') format('woff2');
	font-weight: 400;
	font-display: swap;
}
@font-face
{
	font-family: 'IBM Plex Sans';
	src: url('/fonts/IBMPlexSans-Medium.woff2') format('woff2');
	font-weight: 500;
	font-display: swap;
}
@font-face
{
	font-family: 'IBM Plex Sans';
	src: url('/fonts/IBMPlexSans-SemiBold.woff2') format('woff2');
	font-weight: 600;
	font-display: swap;
}

:root
{
	--plansheet-gorton: 'Gorton Perfected', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
	--plansheet-content: 'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

/* The three-role type system: Gorton for the drafting layer (rules below),
   Plex for everything read, mono for data and code (left to the themes).
   Body sets the content face once; most surfaces inherit it. The 'html body'
   selector (specificity 0,0,2) is deliberate: the Shell view declares a bare
   'body' font-family in its own injected stylesheet, and specificity wins that
   tie regardless of stylesheet order. */

html body { font-family: var(--plansheet-content); }

.ps-input, .ps-input-sm,
.cst-auth-field input,
.cst-set-input,
textarea, select
{
	font-family: var(--plansheet-content);
}

/* ---- Headings: the labels on the sheet --------------------------------- */
/* Mirrors the view selectors so it wins on the cascade at equal specificity.
   1.5px is enough to read as drawing lettering without spreading long,
   multi-word titles ("Organization Prompt Shapes") into the margins. */

.ps-list-head h2,
.ps-board-head h2,
.ps-form h3,
.cst-auth-title,
.cst-set-h,
.cst-set-h3,
.ppl-subhead,
.pps h2, .pps h3,
.ppl-detail-head h3,
.cmty-h1,
.cst-list-title,
.cst-cal-title,
.ps-page-h
{
	font-family: var(--plansheet-gorton);
	text-transform: uppercase;
	letter-spacing: 1.5px;
	font-weight: 700;
}

/* A page title for views that ship without one (Workflow, Prompt Library):
   self-contained size + rhythm so it sits like the Board / Roles titles. */
.ps-page-h
{
	font-size: 22px;
	margin: 0 0 14px;
	color: var(--theme-color-text-primary, currentColor);
}

/* Rendered markdown headings (the documentation reader, and any other
   markdown surface) take the display face too, but keep the author's case
   and a light touch -- these are prose headings, not stamped labels. */
.pict-content h1, .pict-content h2, .pict-content h3,
.pict-content h4, .pict-content h5, .pict-content h6
{
	font-family: var(--plansheet-gorton);
	letter-spacing: 0.3px;
}

/* The documentation content area is prose-styled (readable max-width + generous
   padding) for READING. When it hosts the markdown editor, drop those
   constraints so the editor fills the available width instead of sitting in a
   narrow prose column. (Line wrapping itself is handled inside the editor.) */
.pict-inline-doc-content:has(.pict-mde)
{
	max-width: none;
	padding: 12px 16px;
}

/* ---- The top bar: the drafting title block ----------------------------- */
/* The bar reads as the top cell of the sheet. The brand mark (left) and the
   account chip (right) are both drawn as bordered title-block cells -- a stamp
   cell and a label cell divided by linework -- so they mirror each other and
   echo the lockup on the marketing site. The seam below the bar is crisp
   drawing linework, never a colored accent. Everything here references theme
   tokens, so it follows whatever palette is active. */

/* One crisp linework seam, the bottom edge of the top cell. The selectors the
   theme ships for these land in a later stylesheet, so the brand layer has to
   out-specify them (ID scope), not just outrank them by CSSMap priority. */
#Theme-TopBar > .pict-theme-topbar
{
	border-bottom: 1.5px solid var(--theme-color-border-strong, var(--theme-color-border-default, currentColor));
}

/* pict-section-theme paints a two-tone brand-color accent stripe under the bar
   (the old teal-over-orange). Drop it; the linework seam above is the divider. */
#Theme-TopBar .pict-theme-topbar::after
{
	display: none;
}

/* Left lockup: the brand mark as a two-cell title block -- the P stamp in its
   own cell, the wordmark in the next. */
.pict-theme-brand-mark
{
	height: 32px;
	box-sizing: border-box;
	border: 1.5px solid var(--theme-color-border-strong, currentColor);
	border-radius: 2px;
	gap: 0;
	padding: 0;
	overflow: hidden;
	align-items: stretch;
	cursor: pointer;
}
.pict-theme-brand-mark-icon
{
	display: inline-flex;
	align-items: center;
	padding: 0 7px;
	border-right: 1.5px solid var(--theme-color-border-strong, currentColor);
}
.pict-theme-brand-mark .pict-theme-brand-mark-name
{
	font-family: var(--plansheet-gorton);
	text-transform: uppercase;
	letter-spacing: 4px;
	font-weight: 700;
	font-size: 14px;
	border-bottom: 0;
	/* right pad is lighter than left to absorb the trailing letter-spacing */
	padding: 0 7px 0 11px;
	display: inline-flex;
	align-items: center;
	color: var(--theme-color-text-primary, currentColor);
}

.cst-nav,
.pict-theme-topbar-nav
{
	font-family: var(--plansheet-gorton);
	letter-spacing: 1.5px;
}

/* Right chip: the account cell, drawn to match the lockup -- a label cell
   (your name) and a stamp cell (your avatar), divided by linework. The order
   puts the stamp on the outer edge, mirroring the P on the left. */
#Theme-TopBar-User .cst-user-chip
{
	font-family: var(--plansheet-gorton);
	height: 32px;
	align-self: center;
	border: 1.5px solid var(--theme-color-border-strong, currentColor);
	border-radius: 2px;
	gap: 0;
	padding: 0;
	overflow: hidden;
	align-items: stretch;
}
#Theme-TopBar-User .cst-user-chip .cst-user-name
{
	order: 1;
	text-transform: uppercase;
	letter-spacing: 1.5px;
	font-size: 12px;
	padding: 0 11px;
	max-width: 180px;
	display: inline-flex;
	align-items: center;
	color: var(--theme-color-text-primary, currentColor);
}
#Theme-TopBar-User .cst-user-chip .cst-user-avatar
{
	order: 2;
	padding: 0 7px;
	border-left: 1.5px solid var(--theme-color-border-strong, currentColor);
	display: inline-flex;
	align-items: center;
}

/* ---- Navigation rail: group labels drawn, items read ------------------- */
/* The WORKSPACE / PLAN / DESIGN group headers are drafting labels (Gorton,
   uppercase, letterspaced). The item names and descriptions are content the
   reader scans, so they inherit the Plex content face from body. */

.pict-nav-sb-group-head
{
	font-family: var(--plansheet-gorton);
	text-transform: uppercase;
	letter-spacing: 1.5px;
}
.pict-nav-sb-item-name { font-weight: 500; }

/* ---- Buttons: square, engraved ----------------------------------------- */

.ps-btn, .ps-btn-sm,
.cst-auth-button,
.cst-set-btn, .cst-set-btn-secondary,
.ppl-btn,
.pps-btn,
.ppa-btn,
.cst-acct-act,
.tsr-btn
{
	font-family: var(--plansheet-gorton);
	text-transform: uppercase;
	letter-spacing: 1px;
	border-radius: 3px;
}

/* ---- Inputs: crisp corners, value text stays readable ------------------ */

.ps-input, .ps-input-sm,
.cst-auth-field input,
.cst-set-input,
.ppl-card, .ppl-shared-detail,
.ps-card,
.cst-auth-card,
.cst-set-section
{
	border-radius: 3px;
}

/* Field labels join the drafting layer. */
.cst-auth-field label,
.cst-set-label,
.ps-form label
{
	font-family: var(--plansheet-gorton);
	text-transform: uppercase;
	letter-spacing: 1.2px;
	font-size: 11px;
}

/* ---- Badges: stamped, not pill-shaped ---------------------------------- */

.ps-cell-badge,
.ps-card-kind, .ps-card-pri,
.ps-lane-count,
.ppl-badge,
.ppa-badge,
.pps-badge
{
	font-family: var(--plansheet-gorton);
	text-transform: uppercase;
	letter-spacing: 1px;
	border-radius: 2px;
}

/* ---- Tables: a register, not a card ------------------------------------ */

.ps-table { border-radius: 0; border: 1.5px solid var(--theme-color-border-strong, var(--theme-color-border-default, currentColor)); }
.ps-table th
{
	font-family: var(--plansheet-gorton);
	text-transform: uppercase;
	letter-spacing: 1.5px;
}

/* ---- Board: lanes read as columns on a sheet --------------------------- */

.ps-board-head h2 { letter-spacing: 2px; }
.ps-lane-title, .ps-lane-head
{
	font-family: var(--plansheet-gorton);
	text-transform: uppercase;
	letter-spacing: 1px;
}
.ps-card { border-radius: 3px; }

/* ---- Prompt library tabs ----------------------------------------------- */

.ppl-tab
{
	font-family: var(--plansheet-gorton);
	text-transform: uppercase;
	letter-spacing: 1px;
}

/* The embedded prompt editor (pict-section-prompteditor >= 1.1.1) self-themes
   from the theme's display-font tokens (Typography.Family.Display + the
   Typography.Display treatment block in source/themes/plansheet.json), so its
   titles need no app-side rule here. Other embedded controls can adopt the same
   var(--theme-typography-family-display, ...) pattern to follow the brand. */

/* ---- The sign-in wordmark: the lockup ---------------------------------- */

.cst-auth-wordmark
{
	font-family: var(--plansheet-gorton);
	text-transform: uppercase;
	letter-spacing: 5px;
	font-weight: 700;
}

/* No word-spacing correction: Gorton Perfected is a proportional face with a
   normal space (measured ~0.30em). The free Open Gorton we prototyped with had
   a ~0.74em keycap space that needed a big negative word-spacing; Perfected
   does not. Do not re-add that hack against this font -- it would collide. */

/* ═══ Documentation reading experience ═══════════════════════════════════
   The Foundational Documentation reader (.ps-fdocs) renders markdown into a
   wrapper that carries BOTH .pict-content and .pict-inline-doc-content. Those
   two dependency layers hand it a system body font, a full-bleed measure, and
   tight rhythm. Scoping under .ps-fdocs lets these rules out-specify both
   layers (two classes beats one) and give the docs an intentional, on-brand
   reading column: Plex for the prose, Gorton for the labels, a comfortable
   measure, and drafting linework. Everything is token-based, so light, dark,
   and the costume themes all re-skin. Other markdown surfaces (notebook, help
   drawer, editor preview) are left to a later pass on purpose. */

.ps-fdocs .pict-content
{
	font-family: var(--plansheet-content);
	font-size: 16px;
	line-height: 1.72;
	color: var(--theme-color-text-primary, #2B2D2F);
	max-width: 52rem;
	margin: 0 auto;
	padding: 2.4rem 2.8rem 4.5rem;
	-webkit-font-smoothing: antialiased;
	text-rendering: optimizeLegibility;
}
.ps-fdocs .pict-content > :first-child { margin-top: 0; }
.ps-fdocs .pict-content p { margin: 0 0 1.15em; }
.ps-fdocs .pict-content strong { font-weight: 600; color: var(--theme-color-text-primary, #2B2D2F); }

/* Headings: labels on the sheet. h1/h2 close with a hairline; h4 and below
   drop to small uppercase drafting sublabels. Author case is kept. */
.ps-fdocs .pict-content h1
{
	font-family: var(--plansheet-gorton);
	font-size: 2em; font-weight: 700; line-height: 1.15; letter-spacing: 0.3px;
	margin: 0 0 0.7em; padding-bottom: 0.3em;
	border-bottom: 1.5px solid var(--theme-color-border-strong, #BDB4A4);
	color: var(--theme-color-text-primary, #2B2D2F);
}
.ps-fdocs .pict-content h2
{
	font-family: var(--plansheet-gorton);
	font-size: 1.5em; font-weight: 700; line-height: 1.2; letter-spacing: 0.2px;
	margin: 2.2em 0 0.6em; padding-bottom: 0.28em;
	border-bottom: 1px solid var(--theme-color-border-default, #DDD6CA);
	color: var(--theme-color-text-primary, #2B2D2F);
}
.ps-fdocs .pict-content h3
{
	font-family: var(--plansheet-gorton);
	font-size: 1.22em; font-weight: 700; line-height: 1.25; letter-spacing: 0.2px;
	margin: 1.9em 0 0.5em; color: var(--theme-color-text-primary, #2B2D2F);
}
.ps-fdocs .pict-content h4
{
	font-family: var(--plansheet-gorton);
	font-size: 0.82em; font-weight: 700; text-transform: uppercase; letter-spacing: 1.3px;
	margin: 1.8em 0 0.5em; color: var(--theme-color-text-secondary, #5E5549);
}
.ps-fdocs .pict-content h5, .ps-fdocs .pict-content h6
{
	font-family: var(--plansheet-gorton);
	font-size: 0.78em; font-weight: 700; text-transform: uppercase; letter-spacing: 1px;
	margin: 1.6em 0 0.4em; color: var(--theme-color-text-muted, #8A8F99);
}

/* Links: brand-primary with a restrained underline that firms on hover. */
.ps-fdocs .pict-content a
{
	color: var(--theme-color-brand-primary, #1D5FA8);
	text-decoration: underline;
	text-decoration-thickness: 1px;
	text-underline-offset: 2px;
	text-decoration-color: color-mix(in srgb, var(--theme-color-brand-primary, #1D5FA8) 42%, transparent);
}
.ps-fdocs .pict-content a:hover { text-decoration-color: currentColor; text-decoration-thickness: 2px; }

/* Lists */
.ps-fdocs .pict-content ul, .ps-fdocs .pict-content ol { margin: 0 0 1.15em; padding-left: 1.6em; line-height: 1.7; }
.ps-fdocs .pict-content li { margin: 0.3em 0; }
.ps-fdocs .pict-content li::marker { color: var(--theme-color-text-muted, #8A8F99); }
.ps-fdocs .pict-content li > ul, .ps-fdocs .pict-content li > ol { margin: 0.3em 0; }

/* Code surfaces: a clean, cool, bright plate instead of the warm tan tertiary, which reads as dingy in
   the light palette. The colors are theme variables set on the reader root, so the block-code reset keeps
   its specificity and dark gets a proper dark plate without a per-rule override. */
.ps-fdocs
{
	--ps-code-chip-bg:   #EEF1F6;
	--ps-code-plate-bg:  #F5F7FA;
	--ps-code-gutter-bg: #ECEFF4;
	--ps-code-border:    #E2E5EC;
}
/* Light reader: re-skin the warm tan surfaces to a clean, professional neutral. Scoped to the docs
   reader and to light mode, so the warm app chrome and the dark theme are untouched. Redefining the
   theme background + border tokens here cascades the clean palette to the ToC tree, the reading
   surface, the hairlines, tables, and hover states in one move. */
html.theme-light .ps-fdocs
{
	--theme-color-background-panel:     #FFFFFF;
	--theme-color-background-secondary: #F5F6F8;
	--theme-color-background-tertiary:  #EFF1F4;
	--theme-color-background-hover:     #F1F3F6;
	--theme-color-border-light:         #ECEEF1;
	--theme-color-border-default:       #E3E5E9;
	--theme-color-border-strong:        #CDD1D9;
}
html.theme-dark .ps-fdocs
{
	--ps-code-chip-bg:   #242B35;
	--ps-code-plate-bg:  #1D222B;
	--ps-code-gutter-bg: #171C24;
	--ps-code-border:    #2E3640;
}
/* Inline code: a bright chip that highlights the token. */
.ps-fdocs .pict-content code
{
	font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
	font-size: 0.86em;
	background: var(--ps-code-chip-bg, #F5F7FB);
	border: 1px solid color-mix(in srgb, var(--theme-color-brand-primary, #4F95E8) 20%, var(--ps-code-border, #DEE3EC));
	color: var(--theme-color-text-primary, #2B2D2F);
	padding: 0.08em 0.4em;
	border-radius: 4px;
}
/* Code block plate: wrap + pre share the plate surface, the gutter sits a touch deeper, the border is a
   clean neutral -- overriding the dependency's warm tan. */
.ps-fdocs .pict-content .pict-content-code-wrap,
.ps-fdocs .pict-content .pict-content-code-wrap pre
{
	background: var(--ps-code-plate-bg, #F1F4F9);
}
.ps-fdocs .pict-content .pict-content-code-line-numbers { background: var(--ps-code-gutter-bg, #E8ECF3); }
.ps-fdocs .pict-content .pict-content-code-wrap { border-color: var(--ps-code-border, #DEE3EC); }

/* Code blocks: the dependency's .pict-content-code-wrap already draws the framed,
   rounded plate (border + background), so the container only owns the vertical
   rhythm. Adding a second border here was the "double box". Hold every part of the
   line-number row at one size + leading so the gutter stays aligned (below). */
.ps-fdocs .pict-content .pict-content-code-container
{
	margin: 1.2em 0;
}
.ps-fdocs .pict-content .pict-content-code-wrap,
.ps-fdocs .pict-content .pict-content-code-wrap pre,
.ps-fdocs .pict-content .pict-content-code-wrap code,
.ps-fdocs .pict-content .pict-content-code-line-numbers,
.ps-fdocs .pict-content .pict-content-code-line-numbers span
{
	font-size: 13px;
	line-height: 1.6;
}
.ps-fdocs .pict-content .pict-content-code-wrap code,
.ps-fdocs .pict-content pre code
{
	background: none; border: 0; padding: 0; color: inherit; border-radius: 0;
}

/* Blockquote: a note penciled in the margin. */
.ps-fdocs .pict-content blockquote
{
	border-left: 3px solid var(--theme-color-brand-primary, #1D5FA8);
	background: var(--theme-color-background-secondary, #F7F5F0);
	color: var(--theme-color-text-secondary, #5E5549);
	margin: 1.3em 0; padding: 0.6em 1em; border-radius: 0 4px 4px 0;
}
.ps-fdocs .pict-content blockquote p { margin: 0.3em 0; }

/* Tables: a drafting schedule -- Gorton uppercase headers, crisp hairlines. */
.ps-fdocs .pict-content table
{
	border-collapse: collapse; width: 100%; margin: 1.3em 0; font-size: 0.95em;
	display: block; overflow-x: auto;
}
.ps-fdocs .pict-content table th
{
	font-family: var(--plansheet-gorton); text-transform: uppercase; letter-spacing: 0.7px;
	font-size: 0.82em; font-weight: 700; text-align: left;
	background: var(--theme-color-background-secondary, #F5F0E8);
	border: 1px solid var(--theme-color-border-default, #DDD6CA);
	padding: 0.5em 0.85em; color: var(--theme-color-text-primary, #2B2D2F);
}
.ps-fdocs .pict-content table td
{
	border: 1px solid var(--theme-color-border-default, #DDD6CA);
	padding: 0.5em 0.85em; color: var(--theme-color-text-primary, #2B2D2F); vertical-align: top;
}
.ps-fdocs .pict-content table tr:nth-child(even) td { background: var(--theme-color-background-secondary, #F7F5F0); }

/* Drafting rule + centered figures / diagrams. */
.ps-fdocs .pict-content hr { border: 0; border-top: 1px solid var(--theme-color-border-default, #DDD6CA); margin: 2.4em 0; }
.ps-fdocs .pict-content img { max-width: 100%; height: auto; display: block; margin: 1.5em auto; border-radius: 4px; }
.ps-fdocs .pict-content pre.mermaid { display: flex; justify-content: center; margin: 1.5em 0; background: none; }

/* Diagram SVGs are usually dark ink on a transparent canvas, so they vanish on a
   dark page. Back them with a warm light "paper" card -- a contrasty surface in
   ANY theme -- so the ink always reads, and it frames as an intentional figure.
   Paper is the DEFAULT (the common case never flashes); the reader then inspects
   each SVG's source and tags the ones that paint their own background or are drawn
   in light ink with .pict-content-svg-bare, which strips the card back off. Raster
   photos keep their own background; this targets .svg only. */
.ps-fdocs .pict-content img[src*=".svg"]
{
	background: #FBF8F0;
	padding: 14px 18px;
	border: 1px solid rgba(0, 0, 0, 0.06);
	border-radius: 2px;
	box-sizing: border-box;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
}
.ps-fdocs .pict-content img[src*=".svg"].pict-content-svg-bare
{
	background: none; padding: 0; border: 0; box-shadow: none;
}

/* Code-block controls: crisp drafting corners that echo the plate. */
.ps-fdocs .pict-content .pict-content-code-action-btn { border-radius: 3px; }

/* ═══ Tooltips: drafting cards, not floating blobs ═══════════════════════
   pict-section-modal's rich tooltip (the vocabulary definition popover, and
   every other rich tooltip) defaults its surface to --theme-color-background-
   tertiary, which in a warm light palette reads as a flat tan box. Make it an
   intentional card: the panel surface, a crisp hairline, a cleaner shadow --
   correct in every theme. The arrow reads the same bg var, so it follows; we
   border its two outer edges per position so the seam closes. */
.pict-modal-tooltip
{
	--pict-modal-tooltip-bg: var(--theme-color-background-panel, #FBF8F0);
	border: 1px solid var(--theme-color-border-strong, var(--theme-color-border-default, #BDB4A4));
	box-shadow: 0 10px 28px rgba(0, 0, 0, 0.26);
	border-radius: 4px;
}
.pict-modal-tooltip-arrow { border: 0 solid var(--theme-color-border-strong, var(--theme-color-border-default, #BDB4A4)); }
.pict-modal-tooltip--top .pict-modal-tooltip-arrow    { border-right-width: 1px; border-bottom-width: 1px; }
.pict-modal-tooltip--bottom .pict-modal-tooltip-arrow { border-left-width: 1px;  border-top-width: 1px; }
.pict-modal-tooltip--left .pict-modal-tooltip-arrow   { border-top-width: 1px;   border-right-width: 1px; }
.pict-modal-tooltip--right .pict-modal-tooltip-arrow  { border-bottom-width: 1px; border-left-width: 1px; }

/* The vocabulary definition popover: a small drafting card -- Gorton label on
   the term, Plex for the gloss, the brand link to read the full definition. */
.pict-vocab-popover-title
{
	font-family: var(--plansheet-gorton);
	text-transform: uppercase;
	letter-spacing: 1px;
	font-size: 12px;
}
.pict-vocab-popover-link
{
	font-family: var(--plansheet-gorton);
	text-transform: uppercase;
	letter-spacing: 0.8px;
}

/* ═══ Docs tree relocated into the sidebar ═══════════════════════════════
   The Foundational Docs tree (InlineDoc-Nav) now renders into the sidebar's
   Documentation tab, not the reader's own nav column. Hide the reader's now-empty
   nav column + its resizer so the article fills the pane (true reading mode when
   the sidebar is collapsed). */
.ps-fdocs .pict-inline-doc-nav-container,
.ps-fdocs .pict-inline-doc-resizer { display: none !important; }
.ps-fdocs .pict-inline-doc-content-container { flex: 1 1 auto !important; min-width: 0; }

/* The tree, now inside the sidebar, wears the sidebar's own surface so it reads as
   part of Plansheet: no inner frame, fills the host, inherits the panel. */
.ps-nb-docs-host { height: 100%; overflow: auto; }
.ps-nb-docs-host .pict-inline-doc-nav { border: 0 !important; background: transparent !important; height: 100%; }
`;
